import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin,
  ChevronRight,
  LogOut,
  Eye,
  Sparkles,
  Stethoscope,
  HeartPulse,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

const BRANCH_ICON = {
  PBY: HeartPulse,
  CHP: HeartPulse,
  VZK: HeartPulse,
  BCS: Sparkles,
  BPC: Stethoscope,
  BNE: Eye,
};

export default function BranchSelector() {
  const { user, setBranch, logout } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/branches");
        setBranches(data);
      } catch (err) {
        toast.error(formatApiError(err, "Could not load branches"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const choose = (b) => {
    setBranch(b);
    toast.success(`Branch set: ${b.name}`);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-warm-sand" data-testid="branch-selector-page">
      <header className="sticky top-0 z-10 backdrop-blur bg-[#F9F6F0]/85 border-b border-sand">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size="h-10" />
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-[#5C5652]">Signed in as</span>
              <span className="font-semibold text-forest" data-testid="header-user-name">
                {user?.name} <span className="text-[#5C5652] font-normal">· {user?.role}</span>
              </span>
            </div>
            <button
              onClick={logout}
              data-testid="header-logout-button"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-sand text-[#2A2624] hover:bg-white transition-colors"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 sm:py-14">
        <div className="mb-10 animate-fade-up">
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Step 1 · Choose your branch
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-forest mb-3 leading-tight">
            Which centre are you operating from today?
          </h1>
          <p className="text-[#5C5652] max-w-2xl">
            Your patient list, tokens and dashboards are scoped to the branch you pick here. You
            can switch branches anytime from the top bar.
          </p>
        </div>

        {loading ? (
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-44 rounded-lg bg-white border border-sand animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="branch-grid">
            {branches.map((b, idx) => {
              const Icon = BRANCH_ICON[b.code] || MapPin;
              return (
                <button
                  key={b.code}
                  onClick={() => choose(b)}
                  data-testid={`branch-card-${b.code}`}
                  className="group text-left p-6 rounded-lg bg-white border border-sand hover:border-[#1A4331]/40 hover:-translate-y-0.5 transition-transform transition-colors"
                  style={{ animation: `fadeUp 400ms ease-out ${idx * 60}ms both` }}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="h-11 w-11 rounded-lg bg-[#1A4331]/8 flex items-center justify-center text-forest group-hover:bg-forest group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] font-bold tracking-widest text-[#5C5652] bg-[#F9F6F0] border border-sand px-2 py-1 rounded">
                      {b.code}
                    </span>
                  </div>
                  <h3 className="font-display text-xl text-forest leading-tight mb-1">
                    {b.name}
                  </h3>
                  <p className="text-sm text-[#5C5652] mb-6">{b.tagline}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-sand">
                    <span className="text-xs uppercase tracking-wider text-[#5C5652]">
                      {b.specialty}
                    </span>
                    <span className="inline-flex items-center gap-1 text-forest font-semibold text-sm">
                      Enter
                      <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.75} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
